import { useQuery } from "@tanstack/react-query";
import { useRouterState } from "@tanstack/react-router";
import {
  Suspense,
  createContext,
  lazy,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useConfig } from "../config.tsx";
import { withMessages } from "../i18n.ts";
import { workspacesQuery } from "./data.ts";
import { isApple } from "./quickfind.ts";
import { useWorkspace } from "./useWorkspace.ts";

const loadQuickFind = withMessages(() =>
  import("./QuickFind.tsx").then((m) => ({ default: m.QuickFind })),
);
const QuickFind = lazy(loadQuickFind);

const ContextMenu = lazy(
  withMessages(() =>
    import("../components/ContextMenu.tsx").then((m) => ({ default: m.ContextMenu })),
  ),
);

interface ContextAt {
  x: number;
  y: number;
  target: Element;
}

/** Where a menu opened from the keyboard goes: the text cursor, or the focused control. */
function keyboardPoint(target: Element): { x: number; y: number } {
  const selection = window.getSelection();
  if (selection && selection.rangeCount > 0 && target.contains(selection.anchorNode)) {
    const box = selection.getRangeAt(0).getBoundingClientRect();
    if (box.width > 0 || box.height > 0) return { x: box.left, y: box.bottom };
  }
  const box = target.getBoundingClientRect();
  return { x: box.left + 8, y: box.top + Math.min(box.height, 32) };
}

/** Opens Quick Find; a no-op for someone signed out. */
const QuickFindContext = createContext<() => void>(() => {});

/** Opens Quick Find, for the Sidebar's search button and the like. */
export const useOpenQuickFind = () => useContext(QuickFindContext);

/** The workspace last opened, for Quick Find on pages outside any workspace. */
const LAST_KEY = "paginae.lastWorkspace";

function lastWorkspace(): string | null {
  try {
    return localStorage.getItem(LAST_KEY);
  } catch {
    return null;
  }
}

/**
 * Quick Find and the command palette (ADR-0009) on every page of the app for someone signed in:
 * Ctrl/⌘+K and Ctrl/⌘+P open it, as in Notion (and in place of the browser's own use of them),
 * except while another dialog is open. Inside a workspace it searches that workspace; elsewhere
 * (help, account, administration, inbox) the one last opened, or else the first.
 */
export function QuickFindHost({ children }: { children: ReactNode }) {
  const config = useConfig();
  return config.user ? (
    <SignedIn>{children}</SignedIn>
  ) : (
    <QuickFindContext.Provider value={() => {}}>{children}</QuickFindContext.Provider>
  );
}

function SignedIn({ children }: { children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const here = /^\/w\/([^/]+)/.exec(pathname)?.[1] ?? null;
  const workspaces = useQuery(workspacesQuery);
  useEffect(() => {
    if (!here) return;
    try {
      localStorage.setItem(LAST_KEY, here);
    } catch {
      // Without storage, pages outside workspaces search the first one.
    }
  }, [here]);
  const last = here ? null : lastWorkspace();
  const id =
    here ??
    (last && workspaces.data?.some((w) => w.id === last) ? last : workspaces.data?.[0]?.id) ??
    "";
  const { workspace } = useWorkspace(id);
  const [searching, setSearching] = useState(false);
  // Pāginæ's right-click menu (ADR-0054), unless the person turned it off.
  const [context, setContext] = useState<ContextAt | null>(null);
  const contextOpen = useRef(false);
  const ownMenu = useConfig().user?.contextMenu ?? true;
  useEffect(() => {
    contextOpen.current = context !== null;
  }, [context]);
  useEffect(() => {
    if (!ownMenu) return;
    const onContext = (event: MouseEvent) => {
      // From the keyboard (the menu key, Shift+F10) the event has no pointer.
      const keyboard =
        (event as PointerEvent).pointerType === "" || (event.clientX === 0 && event.clientY === 0);
      // The browser's menu: a second right-click while ours is open, or Shift+right-click.
      if (contextOpen.current) {
        setContext(null);
        return;
      }
      if ((event.shiftKey && !keyboard) || event.defaultPrevented) return;
      const target = event.target instanceof Element ? event.target : null;
      // Dialogs and other menus keep the browser's: their own controls are the way there.
      if (!target || target.closest("[role='dialog'], [role='menu'], [role='listbox']")) return;
      event.preventDefault();
      setContext({
        ...(keyboard ? keyboardPoint(target) : { x: event.clientX, y: event.clientY }),
        target,
      });
    };
    document.addEventListener("contextmenu", onContext);
    return () => document.removeEventListener("contextmenu", onContext);
  }, [ownMenu]);

  useEffect(() => {
    const apple = isApple();
    const onKey = (event: KeyboardEvent) => {
      const mod = apple ? event.metaKey : event.ctrlKey;
      const key = event.key.toLowerCase();
      // Only Ctrl/⌘+K: Ctrl/⌘+P stays the browser's (print), free for later (ADR-0048).
      if (!mod || event.altKey || event.shiftKey || key !== "k") return;
      if (event.defaultPrevented) return;
      if (document.querySelector(".modal-overlay")) return;
      event.preventDefault();
      setSearching(true);
    };
    document.addEventListener("keydown", onKey);
    // Loaded ahead, once the app has settled, so it opens at once: keys typed right after the
    // shortcut must reach the search field, not the page behind it.
    const preload = setTimeout(() => void loadQuickFind(), 2000);
    return () => {
      document.removeEventListener("keydown", onKey);
      clearTimeout(preload);
    };
  }, []);

  const open = useMemo(() => () => setSearching(true), []);
  return (
    <QuickFindContext.Provider value={open}>
      {children}
      {context && (
        <Suspense fallback={null}>
          <ContextMenu
            request={context}
            onClose={() => setContext(null)}
            onPalette={() => setSearching(true)}
          />
        </Suspense>
      )}
      {workspace && searching && (
        <Suspense fallback={null}>
          <QuickFind workspace={workspace} onClose={() => setSearching(false)} />
        </Suspense>
      )}
    </QuickFindContext.Provider>
  );
}
